<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
    style="z-index: 9999"
    @click.self="closeModal"
  >
    <div
      class="bg-white rounded-lg shadow-2xl border-2 border-black max-w-4xl w-full max-h-[90vh] overflow-hidden"
      style="z-index: 10000"
    >
      <!-- Header -->
      <div
        :class="testType === 'pre' ? 'bg-blue-50' : 'bg-purple-50'"
        class="p-6 border-b-2 border-gray-200"
      >
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div
              :class="testType === 'pre' ? 'bg-blue-100' : 'bg-purple-100'"
              class="w-10 h-10 rounded-lg flex items-center justify-center"
            >
              <svg
                :class="testType === 'pre' ? 'text-blue-600' : 'text-purple-600'"
                class="w-6 h-6"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4M11,6V14H13V6H11M11,16V18H13V16H11Z"
                />
              </svg>
            </div>
            <div>
              <h2
                :class="testType === 'pre' ? 'text-blue-900' : 'text-purple-900'"
                class="text-xl font-semibold"
              >
                {{ testType === "pre" ? "Pre-Test Review" : "Post-Test Review" }}
              </h2>
              <p :class="testType === 'pre' ? 'text-blue-700' : 'text-purple-700'" class="text-sm">
                {{ fullTestData?.topic || testDetails?.topic || "Loading..." }}
              </p>
            </div>
          </div>
          <button
            @click="closeModal"
            :class="testType === 'pre' ? 'hover:bg-blue-100' : 'hover:bg-purple-100'"
            class="p-2 rounded-lg transition-colors cursor-pointer"
            aria-label="Close"
          >
            <svg class="w-6 h-6 text-gray-600" fill="currentColor" viewBox="0 0 24 24">
              <path
                d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"
              />
            </svg>
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="p-12 text-center">
        <div
          class="mx-auto mb-4 w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center"
        >
          <svg
            :class="testType === 'pre' ? 'text-blue-600' : 'text-purple-600'"
            class="animate-spin h-8 w-8"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        </div>
        <p class="text-gray-600">Loading test details...</p>
      </div>

      <!-- Content -->
      <div v-else-if="fullTestData" class="overflow-y-auto max-h-[calc(90vh-120px)]">
        <!-- Score Summary -->
        <div class="p-6 bg-gray-50 border-b-2 border-gray-200">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-6">
              <!-- Score Badge -->
              <div
                :class="
                  testType === 'pre'
                    ? 'bg-blue-50 border-blue-300'
                    : 'bg-purple-50 border-purple-300'
                "
                class="w-24 h-24 rounded-full border-4 flex items-center justify-center"
              >
                <div class="text-center">
                  <div
                    :class="testType === 'pre' ? 'text-blue-600' : 'text-purple-600'"
                    class="text-3xl font-bold"
                  >
                    {{ fullTestData.score }}%
                  </div>
                </div>
              </div>

              <!-- Score Details -->
              <div class="space-y-2">
                <div>
                  <span
                    :class="
                      testType === 'pre'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-purple-100 text-purple-700'
                    "
                    class="px-3 py-1 text-sm font-semibold rounded"
                  >
                    {{ getPerformanceMessage() }}
                  </span>
                </div>
                <div class="text-sm text-gray-600">
                  <span class="font-semibold text-gray-900"
                    >{{ correctCount }}/{{ totalQuestions }}</span
                  >
                  questions correct
                </div>
                <div class="text-sm text-gray-600">
                  <span class="font-semibold text-gray-900">{{ wrongCount }}</span> incorrect
                  answers
                </div>
                <div class="text-sm text-gray-500">
                  Subject: <span class="font-medium text-gray-700">{{ fullTestData.subject }}</span>
                </div>
                <div v-if="testDetails?.tutorName" class="text-sm text-gray-500">
                  <span
                    v-if="testDetails.tutorArchived"
                    class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-100 text-orange-700 mr-1"
                  >
                    Archived
                  </span>
                  Tutor:
                  <span
                    class="font-medium"
                    :class="testDetails.tutorArchived ? 'text-gray-400' : 'text-gray-700'"
                    >{{ testDetails.tutorName }}</span
                  >
                </div>
              </div>
            </div>

            <!-- Date -->
            <div class="text-right text-sm text-gray-500">
              <div>Completed:</div>
              <div class="font-medium text-gray-700">
                {{ formatDate(fullTestData.date_completed) }}
              </div>
            </div>
          </div>
        </div>

        <!-- Tab Navigation -->
        <div class="border-b-2 border-gray-200 bg-white">
          <div class="flex">
            <button
              @click="activeTab = 'review'"
              :class="[
                'px-6 py-3 font-medium text-sm border-b-2 transition-colors cursor-pointer',
                activeTab === 'review'
                  ? testType === 'pre'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300',
              ]"
            >
              Answer Review
            </button>
            <button
              @click="activeTab = 'notes'"
              :class="[
                'px-6 py-3 font-medium text-sm border-b-2 transition-colors cursor-pointer',
                activeTab === 'notes'
                  ? testType === 'pre'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300',
              ]"
            >
              Student Notes
            </button>
          </div>
        </div>

        <!-- Tab Content -->
        <div class="p-6">
          <!-- Answer Review Tab -->
          <div v-if="activeTab === 'review'" class="space-y-6">
            <div
              v-for="(question, index) in fullTestData.questions"
              :key="index"
              class="bg-white border-2 rounded-lg p-5"
              :class="
                isAnswerCorrect(index) ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
              "
            >
              <!-- Question Header -->
              <div class="flex items-start gap-3 mb-4">
                <div
                  class="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                  :class="
                    isAnswerCorrect(index)
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  "
                >
                  <span class="font-semibold text-sm">{{ index + 1 }}</span>
                </div>
                <div class="flex-1">
                  <div class="flex items-center gap-2 mb-2">
                    <span
                      class="px-2 py-0.5 text-xs font-semibold rounded"
                      :class="
                        isAnswerCorrect(index)
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      "
                    >
                      {{ isAnswerCorrect(index) ? "✓ CORRECT" : "✗ INCORRECT" }}
                    </span>
                  </div>
                  <p class="text-gray-900 font-medium" v-html="renderLatex(question.question)"></p>
                </div>
              </div>

              <!-- Options -->
              <div class="ml-11 space-y-2 mb-4">
                <div
                  v-for="(option, optIndex) in question.options"
                  :key="optIndex"
                  class="p-3 rounded-lg border-2"
                  :class="getOptionClass(index, optIndex)"
                >
                  <div class="flex items-center gap-2">
                    <span class="font-medium text-sm"
                      >{{ String.fromCharCode(65 + optIndex) }}.</span
                    >
                    <span class="text-sm" v-html="renderLatex(option)"></span>
                    <span
                      v-if="optIndex === question.correctAnswer"
                      class="ml-auto text-green-600 text-xs font-semibold"
                    >
                      ✓ CORRECT ANSWER
                    </span>
                    <span
                      v-else-if="fullTestData.student_answers[index] === optIndex"
                      class="ml-auto text-red-600 text-xs font-semibold"
                    >
                      ✗ YOUR ANSWER
                    </span>
                  </div>
                </div>
              </div>

              <!-- Explanation -->
              <div class="ml-11 bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                <div class="flex items-start gap-2">
                  <svg
                    class="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M11,9H13V7H11M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M11,17H13V11H11V17Z"
                    />
                  </svg>
                  <div>
                    <p class="text-sm font-semibold text-blue-900 mb-1">Explanation:</p>
                    <p class="text-sm text-blue-800" v-html="renderLatex(question.explanation)"></p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Student Notes Tab -->
          <div v-else-if="activeTab === 'notes'" class="space-y-4">
            <!-- Notes Metadata -->
            <div class="bg-gray-50 border-2 border-gray-200 rounded-lg p-4">
              <div class="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span class="text-gray-600">Source Type:</span>
                  <span class="ml-2 font-medium text-gray-900">{{
                    getSourceTypeLabel(fullTestData.source_type)
                  }}</span>
                </div>
                <div v-if="fullTestData.filename">
                  <span class="text-gray-600">Filename:</span>
                  <span class="ml-2 font-medium text-gray-900">{{ fullTestData.filename }}</span>
                </div>
                <div>
                  <span class="text-gray-600">Content Length:</span>
                  <span class="ml-2 font-medium text-gray-900"
                    >{{ fullTestData.input_length?.toLocaleString() || 0 }} characters</span
                  >
                </div>
                <div v-if="fullTestData.student_notes_url">
                  <a
                    :href="fullTestData.student_notes_url"
                    target="_blank"
                    class="text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
                  >
                    <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path
                        d="M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.9 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z"
                      />
                    </svg>
                    Download Original File
                  </a>
                </div>
              </div>
            </div>

            <!-- Notes Content -->
            <div class="bg-white border-2 border-gray-200 rounded-lg p-5">
              <div class="flex items-center gap-2 mb-3">
                <svg class="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 24 24">
                  <path
                    d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"
                  />
                </svg>
                <h3 class="text-lg font-semibold text-gray-900">Student Notes</h3>
              </div>

              <div v-if="fullTestData.student_notes" class="prose max-w-none">
                <pre
                  class="whitespace-pre-wrap text-sm text-gray-700 font-sans bg-gray-50 p-4 rounded border border-gray-200"
                  >{{ fullTestData.student_notes }}</pre
                >
              </div>
              <div v-else class="text-center py-8 text-gray-500">
                <svg
                  class="w-12 h-12 mx-auto mb-3 opacity-50"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20M10,19L12,15H9V10H13V12L11,16H14V19H10Z"
                  />
                </svg>
                <p>No text notes available for this test.</p>
                <p class="text-sm mt-1">The student may have uploaded a file instead.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="hasError" class="p-12 text-center">
        <div
          class="mx-auto mb-4 w-16 h-16 bg-red-100 rounded-full flex items-center justify-center"
        >
          <svg class="w-8 h-8 text-red-600" fill="currentColor" viewBox="0 0 24 24">
            <path
              d="M13,13H11V7H13M13,17H11V15H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"
            />
          </svg>
        </div>
        <p class="text-gray-600">{{ errorMessage }}</p>
        <button
          @click="loadTestDetails"
          :class="
            testType === 'pre'
              ? 'bg-blue-600 hover:bg-blue-700'
              : 'bg-purple-600 hover:bg-purple-700'
          "
          class="mt-4 px-4 py-2 text-white rounded-lg transition-colors"
        >
          Try Again
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { db, doc, getDoc, collection, query, where, getDocs } from "@/firebase";
import { logDebug, logInfo, logError, logSuccess } from "@/utils/secureLogger";
import { renderLatex } from "@/utils/latexRenderer"; // ✅ Import LaTeX renderer
import type { PostTestRecord } from "@/services/postTestService";

interface Props {
  isOpen: boolean;
  testId: string | null;
  testType?: "pre" | "post"; // ✅ Add test type prop
  testDetails?: {
    topic: string;
    subject: string;
    score: number;
    passed: boolean;
    date_completed: Date | string;
    tutorName?: string;
    tutorArchived?: boolean;
  } | null;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  close: [];
}>();

// State
const isLoading = ref(false);
const hasError = ref(false);
const errorMessage = ref("");
const fullTestData = ref<PostTestRecord | null>(null);
const activeTab = ref<"review" | "notes">("review");

// Computed
const correctCount = computed(() => {
  if (!fullTestData.value) return 0;

  const count =
    fullTestData.value.student_answers?.filter((answer, index) => {
      const isCorrect = answer === fullTestData.value?.questions?.[index]?.correctAnswer;
      return isCorrect;
    }).length || 0;

  logDebug("Correct count calculation", {
    count,
    totalAnswers: fullTestData.value.student_answers?.length || 0,
    studentAnswers: fullTestData.value.student_answers,
    correctAnswers: fullTestData.value.questions?.map((q: any) => q.correctAnswer),
  });

  return count;
});

const totalQuestions = computed(() => fullTestData.value?.questions?.length || 0);
const wrongCount = computed(() => totalQuestions.value - correctCount.value);

// ✅ NEW: Encouraging performance messages based on score
const getPerformanceMessage = (): string => {
  if (!fullTestData.value) return "📝 Review Complete";
  const score = fullTestData.value.score;

  if (score >= 90) return "🌟 Excellent Work!";
  if (score >= 80) return "🎯 Great Performance!";
  if (score >= 70) return "✨ Good Job!";
  if (score >= 60) return "👍 Nice Effort!";
  if (score >= 50) return "📚 Keep Learning!";
  return "💪 Keep Practicing!";
};

// Load full test details from Firestore
const loadTestDetails = async () => {
  if (!props.testId) return;

  isLoading.value = true;
  hasError.value = false;
  errorMessage.value = "";

  try {
    const testType = props.testType || "post";
    const collectionName = testType === "pre" ? "preTests" : "post_tests";

    logInfo(`Loading full ${testType}-test details`, {
      testId: props.testId.substring(0, 8) + "***",
      collectionName,
      firestorePath: `${collectionName}/${props.testId}`,
    });

    const testDocRef = doc(db, collectionName, props.testId);
    const testDoc = await getDoc(testDocRef);

    if (!testDoc.exists()) {
      logError(`Document not found in Firestore`, {
        collection: collectionName,
        testId: props.testId,
        testType,
      });
      throw new Error(`${testType === "pre" ? "Pre-test" : "Post-test"} not found`);
    }

    logSuccess(`Document found in Firestore collection: ${collectionName}`);

    const data = testDoc.data();

    // ✅ CRITICAL DEBUG: Log raw Firestore data structure
    logDebug("RAW FIRESTORE DATA KEYS", {
      allKeys: Object.keys(data),
      dataStructure: JSON.stringify(
        Object.keys(data).reduce((acc, key) => {
          acc[key] = typeof data[key];
          return acc;
        }, {} as any)
      ),
    });

    // ✅ Transform pre-test data to match post-test structure
    if (testType === "pre") {
      // ✅ CRITICAL FIX: Handle BOTH old and new pre-test formats
      // NEW format: score is percentage (0-100), student_answers is array
      // OLD format: userAnswer in each question object

      let studentAnswersArray: number[];
      let scorePercentage: number;

      if (data.student_answers && Array.isArray(data.student_answers)) {
        // NEW FORMAT: Has student_answers array
        studentAnswersArray = data.student_answers;
        // ✅ CRITICAL FIX: Use percentage field if available, convert score if not
        if (data.percentage !== undefined && data.percentage !== null) {
          scorePercentage = data.percentage; // Use percentage field (0-100)
        } else {
          const rawScore = data.score || 0;
          scorePercentage = rawScore <= 10 ? rawScore * 10 : rawScore; // Convert if needed
        }
      } else if (data.questions && Array.isArray(data.questions)) {
        // OLD FORMAT: Extract userAnswer from each question
        studentAnswersArray = data.questions.map((q: any) => q.userAnswer ?? -1);
        // Calculate percentage from questions
        const correctCount = studentAnswersArray.filter(
          (answer, idx) => answer === data.questions[idx]?.correctAnswer
        ).length;
        scorePercentage =
          data.questions.length > 0 ? Math.round((correctCount / data.questions.length) * 100) : 0;
      } else {
        // NO QUESTIONS DATA - Cannot display test
        logError("Pre-test has no questions data", {
          hasQuestions: !!data.questions,
          hasStudentAnswers: !!data.student_answers,
          dataKeys: Object.keys(data),
        });
        throw new Error("This pre-test cannot be displayed - it has no questions or answer data");
      }

      fullTestData.value = {
        ...data,
        post_test_id: testDoc.id,
        score: scorePercentage,
        passed: scorePercentage >= 60,
        questions: data.questions || [], // Include questions array
        student_answers: studentAnswersArray,
        date_completed: data.date_completed || data.completedAt || data.date_generated,
        topic: data.topic || "Pre-Test Assessment",
        subject: data.subject || "General",
        student_notes: data.studentNotes || data.learningGoals || "",
        source_type: data.source_type || "note",
        input_length: data.input_length || 0,
        filename: data.filename || undefined,
      } as PostTestRecord;
    } else {
      // ✅ CRITICAL FIX: Handle post-test score/percentage fields properly
      // Use percentage field if available, otherwise convert score to percentage
      let postScorePercentage: number;
      if (data.percentage !== undefined && data.percentage !== null) {
        postScorePercentage = data.percentage; // Use percentage field (0-100)
      } else {
        const rawScore = data.score || 0;
        postScorePercentage = rawScore <= 10 ? rawScore * 10 : rawScore; // Convert if needed
      }

      fullTestData.value = {
        ...data,
        post_test_id: testDoc.id,
        topic: data.subject ? `${data.subject} Post-Test` : "Post-Test Assessment",
        subject: data.subject || "General",
        score: postScorePercentage, // ✅ Now uses percentage (0-100) for display
        passed: data.passed !== undefined ? data.passed : postScorePercentage >= 60,
      } as PostTestRecord;

      // ✅ Load pre-test student notes for post-tests (since they test the same material)
      if (data.session_id) {
        try {
          const preTestsRef = collection(db, "preTests");
          const preTestQuery = query(preTestsRef, where("sessionId", "==", data.session_id));
          const preTestSnapshot = await getDocs(preTestQuery);

          if (!preTestSnapshot.empty) {
            const preTestData = preTestSnapshot.docs[0].data();
            // Copy student notes from pre-test
            fullTestData.value.student_notes =
              preTestData.studentNotes || preTestData.learningGoals || "";
            fullTestData.value.source_type = preTestData.source_type || "note";
            fullTestData.value.input_length = preTestData.input_length || 0;
            fullTestData.value.filename = preTestData.filename || undefined;
            fullTestData.value.student_notes_url = preTestData.student_notes_url || undefined;
          }
        } catch (error) {
          logError("Error loading pre-test notes for post-test", error);
        }
      }
    }

    logSuccess(`${testType === "pre" ? "Pre-test" : "Post-test"} details loaded successfully`, {
      questions: fullTestData.value.questions?.length || 0,
      hasNotes: !!fullTestData.value.student_notes,
      fullTestDataExists: !!fullTestData.value,
      score: fullTestData.value.score,
      studentAnswersCount: fullTestData.value.student_answers?.length || 0,
    });

    // ✅ CRITICAL DEBUG: Log the actual fullTestData to confirm it's populated
    logDebug("fullTestData populated", {
      hasData: !!fullTestData.value,
      topicExists: !!fullTestData.value?.topic,
      questionsExist: !!fullTestData.value?.questions,
      questionsLength: fullTestData.value?.questions?.length || 0,
    });
  } catch (error) {
    logError("Error loading test details", error);
    hasError.value = true;
    errorMessage.value = "Failed to load test details. Please try again.";
  } finally {
    isLoading.value = false;
  }
};

// Watch for modal open - MUST be after loadTestDetails is defined
watch(
  () => props.isOpen,
  (newVal) => {
    if (newVal && props.testId) {
      loadTestDetails();
    } else {
      // Reset state when closing
      activeTab.value = "review";
    }
  },
  { immediate: true } // ✅ FIX: Load data immediately when component mounts with isOpen=true
);

// Helper functions
const isAnswerCorrect = (questionIndex: number): boolean => {
  if (!fullTestData.value) return false;
  const studentAnswer = fullTestData.value.student_answers?.[questionIndex];
  const correctAnswer = fullTestData.value.questions?.[questionIndex]?.correctAnswer;
  return studentAnswer === correctAnswer;
};

const getOptionClass = (questionIndex: number, optionIndex: number): string => {
  if (!fullTestData.value) return "border-gray-200 bg-white";

  const question = fullTestData.value.questions[questionIndex];
  const studentAnswer = fullTestData.value.student_answers[questionIndex];
  const correctAnswer = question.correctAnswer;

  // Correct answer (always highlight in green)
  if (optionIndex === correctAnswer) {
    return "border-green-300 bg-green-100";
  }

  // Student's wrong answer
  if (optionIndex === studentAnswer && studentAnswer !== correctAnswer) {
    return "border-red-300 bg-red-100";
  }

  return "border-gray-200 bg-white";
};

const getSourceTypeLabel = (sourceType: string): string => {
  const labels: Record<string, string> = {
    pdf: "PDF Document",
    txt: "Text File",
    docx: "Word Document",
    note: "Pasted Text",
  };
  return labels[sourceType] || sourceType;
};

const formatDate = (date: Date | string | any): string => {
  try {
    const dateObj = date?.toDate ? date.toDate() : date instanceof Date ? date : new Date(date);
    return dateObj.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "Unknown";
  }
};

const closeModal = () => {
  emit("close");
};
</script>

<style scoped>
/* Custom scrollbar */
.overflow-y-auto::-webkit-scrollbar {
  width: 8px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* ✅ LaTeX/KaTeX Rendering Styles */
:deep(.katex-display-wrapper) {
  margin: 1rem 0;
  text-align: center;
  overflow-x: auto;
}

:deep(.katex-inline-wrapper) {
  display: inline;
}

:deep(.latex-error) {
  color: #cc0000;
  font-style: italic;
  background-color: #fff3cd;
  padding: 2px 4px;
  border-radius: 3px;
}

/* KaTeX math rendering */
:deep(.katex) {
  font-size: 1.1em;
}

:deep(.katex-display) {
  margin: 1rem 0;
  overflow-x: auto;
  overflow-y: hidden;
}
</style>
